/**
 * D-EZ-95 — MILESTONES, STREAKS AND DROUGHTS.
 *
 * Read in race order across the whole league. Pre-league priors (D-EZ-97) are
 * counted in where a scenario supplied them, so a veteran's 500th start is his
 * real 500th, not his 500th in this league — and where they are unknown, the
 * count is the league's own and says so (`counting: 'league'`).
 */

import type { CareerPrior } from './types.ts';
import type { HistoryRace } from './source.ts';

export const MILESTONE_STARTS: readonly number[] = [100, 200, 250, 300, 400, 500, 600, 700, 750, 800, 900, 1000];
export const MILESTONE_WINS: readonly number[] = [10, 25, 50, 75, 100, 150, 200];
/** A past winner who has gone this many starts without one is in a drought. */
export const DROUGHT_STARTS = 36;

export type MilestoneKind = 'first-start' | 'first-win' | 'first-pole' | 'nth-start' | 'nth-win' | 'drought-ended';

export interface Milestone {
  kind: MilestoneKind;
  driverId: string;
  name: string;
  /** The count reached: the 100th start, the 25th win; the drought's length for 'drought-ended'. */
  n: number;
  seasonNumber: number;
  raceNumber: number;
  raceName: string;
  /** 'career' when priors were counted in for this driver, else 'league'. */
  counting: 'career' | 'league';
  text: string;
}

interface Running {
  name: string;
  starts: number;
  wins: number;
  poles: number;
  counting: 'career' | 'league';
  winStreak: number;
  top5Streak: number;
  top10Streak: number;
  finishStreak: number;
  startsSinceWin: number;
  lastWin: { seasonNumber: number; raceNumber: number; raceName: string } | null;
}

function start(name: string, prior: CareerPrior | undefined): Running {
  const hasPrior = prior !== undefined && (prior.starts !== undefined || prior.wins !== undefined);
  return {
    name,
    starts: prior?.starts ?? 0,
    wins: prior?.wins ?? 0,
    poles: prior?.poles ?? 0,
    counting: hasPrior ? 'career' : 'league',
    winStreak: 0,
    top5Streak: 0,
    top10Streak: 0,
    finishStreak: 0,
    startsSinceWin: 0,
    lastWin: null,
  };
}

function ord(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

/** Walk every race once; return the achieved milestones and each driver's running tallies. */
function walk(
  races: readonly HistoryRace[],
  priors: Readonly<Record<string, CareerPrior>>,
): { milestones: Milestone[]; running: Map<string, Running> } {
  const running = new Map<string, Running>();
  const milestones: Milestone[] = [];
  const ordered = [...races].sort((a, b) => a.leagueRaceIndex - b.leagueRaceIndex);
  for (const r of ordered) {
    for (const l of r.lines) {
      let d = running.get(l.driverId);
      if (!d) {
        d = start(l.name, priors[l.driverId]);
        running.set(l.driverId, d);
      }
      d.name = l.name;
      const at = { seasonNumber: r.seasonNumber, raceNumber: r.raceNumber, raceName: r.raceName };
      const note = (kind: MilestoneKind, n: number, text: string): void => {
        milestones.push({ kind, driverId: l.driverId, name: l.name, n, ...at, counting: d!.counting, text });
      };
      d.starts += 1;
      if (d.starts === 1) note('first-start', 1, `${l.name} makes his first start.`);
      if (MILESTONE_STARTS.includes(d.starts)) note('nth-start', d.starts, `${l.name} makes his ${ord(d.starts)} start.`);
      if (l.start === 1) {
        d.poles += 1;
        if (d.poles === 1) note('first-pole', 1, `${l.name} wins his first pole.`);
      }
      if (l.finish === 1) {
        if (d.lastWin && d.startsSinceWin >= DROUGHT_STARTS) {
          note('drought-ended', d.startsSinceWin, `${l.name} ends a ${d.startsSinceWin}-start drought.`);
        }
        d.wins += 1;
        if (d.wins === 1) note('first-win', 1, `${l.name} wins for the first time.`);
        else if (MILESTONE_WINS.includes(d.wins)) note('nth-win', d.wins, `${l.name} wins for the ${ord(d.wins)} time.`);
        d.winStreak += 1;
        d.startsSinceWin = 0;
        d.lastWin = at;
      } else {
        d.winStreak = 0;
        d.startsSinceWin += 1;
      }
      d.top5Streak = l.finish <= 5 ? d.top5Streak + 1 : 0;
      d.top10Streak = l.finish <= 10 ? d.top10Streak + 1 : 0;
      d.finishStreak = l.running ? d.finishStreak + 1 : 0;
    }
  }
  return { milestones, running };
}

/** Every milestone reached in the league's history, oldest first. */
export function careerMilestones(
  races: readonly HistoryRace[],
  priors: Readonly<Record<string, CareerPrior>> = {},
): Milestone[] {
  return walk(races, priors).milestones;
}

export interface DriverStreaks {
  driverId: string;
  name: string;
  starts: number;
  wins: number;
  /** Consecutive starts, ending with his most recent one. */
  winStreak: number;
  top5Streak: number;
  top10Streak: number;
  /** Consecutive starts running at the flag. */
  finishStreak: number;
  /** Starts since his last win; for a driver who has never won, his starts. */
  startsSinceWin: number;
  lastWin: { seasonNumber: number; raceNumber: number; raceName: string } | null;
  /** A past winner gone `DROUGHT_STARTS` or more without one. */
  inDrought: boolean;
  counting: 'career' | 'league';
}

/** Where every driver's streaks stand after the last race in `races`. */
export function driverStreaks(
  races: readonly HistoryRace[],
  priors: Readonly<Record<string, CareerPrior>> = {},
): Record<string, DriverStreaks> {
  const { running } = walk(races, priors);
  const out: Record<string, DriverStreaks> = {};
  for (const [id, d] of running) {
    out[id] = {
      driverId: id,
      name: d.name,
      starts: d.starts,
      wins: d.wins,
      winStreak: d.winStreak,
      top5Streak: d.top5Streak,
      top10Streak: d.top10Streak,
      finishStreak: d.finishStreak,
      startsSinceWin: d.startsSinceWin,
      lastWin: d.lastWin,
      inDrought: d.lastWin !== null && d.startsSinceWin >= DROUGHT_STARTS,
      counting: d.counting,
    };
  }
  return out;
}

export type MilestoneInPlayKind = 'nth-start' | 'first-win' | 'nth-win' | 'drought' | 'win-streak' | 'top10-streak';

/** Something a driver could reach in the NEXT race, for the booth to mention. */
export interface MilestoneInPlay {
  kind: MilestoneInPlayKind;
  driverId: string;
  name: string;
  /** The number at stake: start #, the win it would be, the drought or streak length. */
  n: number;
  counting: 'career' | 'league';
  text: string;
}

/**
 * What is in play for these drivers next race, from the history so far. Only
 * facts: "this is his 100th start" if he takes the green; "a win would be his
 * first / his 25th"; "winless in 41 starts"; "three wins in a row".
 */
export function milestonesInPlay(
  races: readonly HistoryRace[],
  driverIds: readonly string[],
  priors: Readonly<Record<string, CareerPrior>> = {},
  names: (id: string) => string = (id) => id,
): MilestoneInPlay[] {
  const streaks = driverStreaks(races, priors);
  const out: MilestoneInPlay[] = [];
  for (const id of driverIds) {
    const s = streaks[id];
    const name = s?.name ?? names(id);
    const counting = s?.counting ?? (priors[id] ? 'career' : 'league');
    const starts = s?.starts ?? priors[id]?.starts ?? 0;
    const wins = s?.wins ?? priors[id]?.wins ?? 0;
    if (MILESTONE_STARTS.includes(starts + 1)) {
      out.push({ kind: 'nth-start', driverId: id, name, n: starts + 1, counting, text: `${name} makes his ${ord(starts + 1)} start if he takes the green.` });
    }
    if (wins === 0 && starts >= 1) {
      out.push({ kind: 'first-win', driverId: id, name, n: starts, counting, text: `${name} is still looking for his first win, ${starts} start${starts === 1 ? '' : 's'} in.` });
    } else if (MILESTONE_WINS.includes(wins + 1)) {
      out.push({ kind: 'nth-win', driverId: id, name, n: wins + 1, counting, text: `A win would be the ${ord(wins + 1)} of ${name}'s career.` });
    }
    if (s?.inDrought) {
      out.push({ kind: 'drought', driverId: id, name, n: s.startsSinceWin, counting, text: `${name} has not won in ${s.startsSinceWin} starts.` });
    }
    if (s && s.winStreak >= 2) {
      out.push({ kind: 'win-streak', driverId: id, name, n: s.winStreak, counting, text: `${name} has won ${s.winStreak} in a row.` });
    }
    if (s && s.top10Streak >= 8) {
      out.push({ kind: 'top10-streak', driverId: id, name, n: s.top10Streak, counting, text: `${name} has ${s.top10Streak} straight top-ten finishes.` });
    }
  }
  return out;
}
